import { useEffect, useState } from "react";
import { Plus, Pencil, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, StatusBadge, EmptyState, Spinner } from "@/components/common";
import MasterDialog, { MASTER_CFG } from "@/components/MasterDialog";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const COLS = {
  vendors: [["code", "Code", "num"], ["name", "Vendor"], ["vat_number", "VAT number", "num"], ["contact_person", "Contact"], ["mobile", "Mobile"]],
  items: [["code", "Code", "num"], ["name", "Item"], ["category", "Category"], ["description", "Description"]],
};

export default function Masters({ kind }) {
  const { user } = useAuth();
  const cfg = MASTER_CFG[kind];
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);

  const load = () => api.get(cfg.endpoint, { params: { q, limit: 1000 } }).then(({ data }) => setRows(data));
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <PageHeader eyebrow="Masters" title={kind === "vendors" ? "Vendor master" : "Product / item master"}
        subtitle="Shared master list used for type-ahead during bill entry. Duplicates are flagged before saving."
        actions={<Button onClick={() => setEdit({})} className="bg-emerald-800 hover:bg-emerald-900" data-testid={`add-${kind}-button`}><Plus className="w-4 h-4 mr-1.5" />Add {cfg.singular.toLowerCase()}</Button>} />
      <div className="panel overflow-hidden fade-up">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
          <div className="relative flex-1 max-w-md"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" placeholder={`Search ${kind}…`} data-testid={`${kind}-search-input`} /></div>
          <span className="ml-auto num text-xs text-slate-500">{rows?.length ?? 0} records</span>
        </div>
        <div className="overflow-x-auto">
          {!rows ? <Spinner /> : !rows.length ? <EmptyState title={`No ${kind} yet`} /> : (
            <table className="w-full" data-testid={`${kind}-table`}>
              <thead><tr>{COLS[kind].map(([, h]) => <th key={h} className="th">{h}</th>)}<th className="th">Created</th><th className="th">Status</th><th className="th" /></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className="row-hover" data-testid={`${kind}-row-${i}`}>
                    {COLS[kind].map(([k, , t]) => <td key={k} className={`td ${t === "num" ? "num text-xs" : ""} ${k === "name" ? "font-medium" : ""}`}>{r[k] || "—"}</td>)}
                    <td className="td text-[11px] text-slate-500"><div>{r.created_by_name}</div><div className="num">{fmtDateTime(r.created_at)}</div></td>
                    <td className="td"><StatusBadge status={r.status} /></td>
                    <td className="td text-right">{user.role === "super_admin" && <Button size="sm" variant="ghost" onClick={() => setEdit(r)} data-testid={`${kind}-edit-${i}`}><Pencil className="w-4 h-4" /></Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <MasterDialog kind={kind} open={!!edit} onOpenChange={(o) => !o && setEdit(null)} initial={edit?.id ? edit : null} onSaved={load} />
    </div>
  );
}
